import React, { useEffect, useState } from "react";
import { Button } from "../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../design/dialog";
import { Input } from "../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../design/fieldset";
import { useParticipant } from "../contexts/ParticipantContext";
import { Participant } from "../models/Participant";
import { Textarea } from "../design/textarea";
import { Select } from "../design/select";
import { SideType, sideTypes } from "../models/SideType";
import { ParticipantType, participantTypes } from "../models/ParticipantType";
import { ParticipantRole, participantRoles } from "../models/ParticipantRole";
import { PlusIcon } from "@heroicons/react/16/solid";
import { Checkbox, CheckboxField } from "../design/checkbox";

interface Props {
  className?: string;
  children?: React.ReactNode;
  participant?: Participant;
  onParticipantCreated?: (participantId: string) => void;
}

interface Errors {
  name?: string;
  type?: string;
  role?: string;
  side?: string;
}

const ParticipantButton: React.FC<Props> = ({
  className,
  children,
  participant,
  onParticipantCreated,
}) => {
  const { createParticipant, updateParticipant } = useParticipant();
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  const [name, setName] = useState<string>("");
  const [type, setType] = useState<ParticipantType>("");
  const [role, setRole] = useState<ParticipantRole>("");
  const [side, setSide] = useState<SideType>("");
  const [details, setDetails] = useState<string>("");

  const [errors, setErrors] = useState<Errors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingMore, setIsCreatingMore] = useState(false);

  useEffect(() => {
    if (participant && isOpen) {
      setName(participant.name ?? "");
      setType(participant.type ?? "");
      setRole(participant.role ?? "");
      setSide(participant.side ?? "");
      setDetails(participant.details ?? "");
    }
  }, [participant, isOpen]);

  const validateForm = () => {
    const newErrors: Errors = {};
    if (!name.trim()) newErrors.name = "A name is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const clearForm = (blockClose = false) => {
    if (!participant) {
      setName("");
      setType("");
      setRole("");
      setSide("");
      setDetails("");
    }
    if (!blockClose) setIsOpen(false);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);

    try {
      if (participant) {
        await updateParticipant(participant._id, file, {
          name,
          type,
          role,
          side,
          details,
        });
      } else {
        const participantId = await createParticipant(file, {
          name,
          type,
          role,
          side,
          details,
        });
        if (onParticipantCreated) {
          onParticipantCreated(participantId);
        }
      }
      clearForm(isCreatingMore);
    } catch (error) {
      console.error("Error submitting participantId:", error);
      alert("Failed to submit participant. Please try again later.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        className={className}
        onClick={() => setIsOpen(true)}
      >
        {children ?? (
          <div className="flex items-center gap-2">
            {!participant && <PlusIcon className="w-4 h-4" />}
            <span className="hidden sm:block">
              {participant ? "Edit Participant" : "Create Participant"}
            </span>
          </div>
        )}
      </Button>

      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>
          {participant ? "Edit Participant" : "Create Participant"}
        </DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Image</Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
              </Field>

              <Field>
                <Label>Name</Label>
                <Input
                  type="text"
                  value={name}
                  placeholder="Name"
                  onChange={(e) => setName(e.target.value)}
                />
                {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
              </Field>

              <Field>
                <Label>Type</Label>
                <Select
                  value={type}
                  onChange={(e) => setType(e.target.value as ParticipantType)}
                >
                  <option value="" disabled>
                    Select type
                  </option>
                  {Object.entries(participantTypes).map(([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  ))}
                </Select>
                {errors.type && <ErrorMessage>{errors.type}</ErrorMessage>}
              </Field>

              <Field>
                <Label>Role</Label>
                <Select
                  value={role}
                  onChange={(e) => setRole(e.target.value as ParticipantRole)}
                >
                  <option value="" disabled>
                    Select role
                  </option>
                  {Object.entries(participantRoles).map(([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  ))}
                </Select>
                {errors.role && <ErrorMessage>{errors.role}</ErrorMessage>}
              </Field>

              <Field>
                <Label>Side</Label>
                <Select
                  value={side}
                  onChange={(e) => setSide(e.target.value as SideType)}
                >
                  <option value="" disabled>
                    Select side
                  </option>
                  {Object.entries(sideTypes).map(([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  ))}
                </Select>
                {errors.side && <ErrorMessage>{errors.side}</ErrorMessage>}
              </Field>

              <Field>
                <Label>Details</Label>
                <Textarea
                  rows={5}
                  value={details}
                  placeholder="Details"
                  onChange={(e) => setDetails(e.target.value)}
                />
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>

        <DialogActions className="justify-between">
          {!participant && (
            <CheckboxField>
              <Checkbox
                checked={isCreatingMore}
                onChange={(checked) => setIsCreatingMore(checked)}
              />
              <Label>Create more</Label>
            </CheckboxField>
          )}
          <div className="flex gap-2">
            <Button plain onClick={() => clearForm(false)}>
              Cancel
            </Button>
            <Button disabled={isSaving} onClick={handleSubmit}>
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ParticipantButton;
